import { FC, HTMLAttributes } from "react";
import {
  BlocksRenderer,
  type BlocksContent,
} from "@strapi/blocks-react-renderer";
import clsx from "clsx";

// ------------------------------------------------------------------------------------------
// NOTE: file name is legacy ("TextWithTabsSection") — the Strapi block is
// still `blocks.text-with-tabs-section`, so we keep the component name to
// avoid disrupting the schema. The UI is now a card grid, not tabs:
// hiding 3 of 4 industries behind tab interaction added no value for short
// comparative content and made spacing fragile to Strapi authoring.
// ------------------------------------------------------------------------------------------

export type ITab = {
  heading?: string;
  description?: BlocksContent;
};

export type ITextWithTabsSectionProps = HTMLAttributes<HTMLElement> & {
  heading?: string;
  subheading?: string;
  tabs?: ITab[];
};

// Normalize Strapi paragraph blocks so every card has the same structure
// regardless of how the author entered content:
//
//   1. SPLIT on `\n` — soft returns (Shift+Enter) in Strapi appear as
//      `\n` inside text nodes, which BlocksRenderer turns into <br>. That
//      <br> creates ~1 line of vertical gap, so cards authored with soft
//      returns look very different from cards with separate paragraphs.
//      Splitting paragraphs at `\n` boundaries gives every line its own
//      <p> block — then `flex gap-2` controls the spacing uniformly.
//
//   2. STRIP empty paragraphs — extra blank lines (Enter twice) produce
//      empty paragraph blocks that add inconsistent gaps card-to-card.
const normalizeParagraphs = (blocks: BlocksContent): BlocksContent => {
  const result: BlocksContent = [];

  for (const block of blocks) {
    if (block.type !== "paragraph") {
      result.push(block);
      continue;
    }

    // Split this paragraph's children into groups separated by `\n`.
    // Each group becomes its own paragraph block in the output.
    let current: typeof block.children = [];
    const flush = () => {
      if (current.length > 0) {
        result.push({ type: "paragraph", children: current });
        current = [];
      }
    };

    for (const child of block.children) {
      if (child.type === "text" && child.text.includes("\n")) {
        const parts = child.text.split("\n");
        parts.forEach((part, i) => {
          if (i > 0) flush();
          if (part.length > 0) {
            current.push({ ...child, text: part });
          }
        });
      } else {
        current.push(child);
      }
    }
    flush();
  }

  // Drop any paragraphs that ended up empty / whitespace-only.
  return result.filter((block) => {
    if (block.type !== "paragraph") return true;
    const text = block.children
      .map((child) => ("text" in child ? child.text : ""))
      .join("");
    return text.trim().length > 0;
  });
};

const TextWithTabsSection: FC<ITextWithTabsSectionProps> = (props) => {
  const { heading, subheading, tabs, className, ...other } = props;

  const cards = tabs?.filter((tab) => !!tab.heading) ?? [];

  return (
    <section
      className={clsx("py-8 md:py-10 lg:py-14 xl:py-16", className)}
      {...other}
    >
      <div className="container mx-auto">
        {/* ---------- Section header ---------- */}
        {(heading || subheading) && (
          <div className="mb-6 md:mb-8 lg:mb-10">
            {heading && (
              <>
                <h2 className="max-w-3xl font-bricolage-grotesque text-[clamp(1.375rem,2vw+0.75rem,2.5rem)] font-medium !leading-[1.15] text-black-900">
                  {heading}
                </h2>
                <div
                  aria-hidden="true"
                  className="mt-3 h-0.5 w-12 rounded-full bg-yellow-400"
                />
              </>
            )}
            {subheading && (
              <h3 className="mt-3 max-w-3xl font-bricolage-grotesque text-[clamp(0.9375rem,0.75vw+0.8rem,1.25rem)] font-normal !leading-snug text-black-700 md:mt-4">
                {subheading}
              </h3>
            )}
          </div>
        )}

        {/* ---------- Card grid ---------- */}
        {cards.length > 0 && (
          <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2 md:gap-4 lg:gap-5">
            {cards.map((tab, idx) => (
              <article
                key={idx}
                className="group relative flex flex-col overflow-hidden rounded-xl border border-black-100 bg-white p-4 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-md sm:p-5 lg:p-6"
              >
                {/* Industry name */}
                <h4 className="font-bricolage-grotesque text-[clamp(1rem,0.5vw+0.875rem,1.25rem)] font-medium !leading-tight text-black-900">
                  {tab.heading}
                </h4>

                {/* Yellow accent line — grows on hover for subtle motion */}
                <div
                  aria-hidden="true"
                  className="mt-2 h-0.5 w-8 rounded-full bg-yellow-400 transition-all duration-300 group-hover:w-12"
                />

                {/* Description — flex+gap controls Impact↔Misuse rhythm */}
                {tab.description && (
                  <div
                    className={clsx(
                      "mt-3 flex flex-col gap-2",
                      "text-[clamp(0.8125rem,0.2vw+0.75rem,0.9375rem)] leading-relaxed text-black-700",
                      "[&_p]:m-0",
                      "[&_strong]:font-medium [&_strong]:text-black-900",
                      "[&_a]:text-primary-900 [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-primary-800",
                      "[&_ul]:my-0 [&_ul]:list-disc [&_ul]:pl-5",
                      "[&_ol]:my-0 [&_ol]:list-decimal [&_ol]:pl-5",
                      "[&_li]:m-0 [&_li+li]:mt-1.5",
                    )}
                  >
                    <BlocksRenderer
                      content={normalizeParagraphs(tab.description)}
                    />
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default TextWithTabsSection;
